<template>
  <v-dialog
    content-class="mfa-verification-modal"
    :value="visible"
    @keydown.esc="onBack"
    @click:outside="onBack"
    :max-width="450"
    persistent
  >
    <v-card class="popup-wrapper">
      <div class="close-action">
        <span class="close_btn" @click="onBack">
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>

      <div class="modal-content">
        <div class="title">Confirm Your Identity</div>
        <div class="description">
          To complete login process provide the code generated by authentication application you used while registering your account.
        </div>

        <div class="mfa-section">
          <otp-input
            :key="otpInputKey"
            input-classes="otp-input"
            separator=""
            :num-inputs="6"
            :should-auto-focus="true"
            :is-input-num="true"
            class="otp-code"
            :class="{ 'incorrect-code': error }"
            @on-change="codeChanged"
            @on-complete="codeCompleted"
          />

          <div v-if="error" class="error-message">
            {{ error }}
          </div>
        </div>

        <div class="buttons">
          <v-btn
            small
            depressed
            class="back-button"
            @click="onBack"
          >
            {{ $t('back') }}
          </v-btn>
          <v-btn
            small
            depressed
            class="verify-button"
            @click="handleVerify"
            :disabled="loading || !mfaCode || mfaCode.length !== 6"
          >
            {{ loading ? 'Verifying...' : 'Submit' }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import OtpInput from '@bachdgvn/vue-otp-input';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'MfaVerificationModal',
  components: {
    OtpInput,
  },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      mfaCode: '',
      inactivityTimer: null,
      otpInputKey: 0,
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Clear any previous state
        this.mfaCode = '';
        this.$emit('clear-error');

        // Force re-render of OTP input component
        this.otpInputKey += 1;

        // Use a slight delay to ensure the component is fully rendered
        this.$nextTick(() => {
          setTimeout(() => {
            this.clearMfaCode();
          }, 100);
        });
      }
    },
  },
  mounted() {
    // Clear MFA code after 2 minutes of inactivity
    this.startInactivityTimer();
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    codeChanged() {
      this.mfaCode = '';
      this.$emit('clear-error');
      // Restart inactivity timer on user interaction
      this.startInactivityTimer();
    },
    codeCompleted(code) {
      this.mfaCode = code;
      // Auto-submit when code is complete
      if (code && code.length === 6 && !this.loading) {
        this.handleVerify();
      }
      // Restart inactivity timer on user interaction
      this.startInactivityTimer();
    },
    handleVerify() {
      if (this.mfaCode && this.mfaCode.length === 6) {
        this.$emit('verify', this.mfaCode);
      }
    },
    onBack() {
      this.$emit('back');
    },
    clearMfaCode() {
      this.mfaCode = '';
      // Force the OTP input component to clear by triggering a re-render
      this.$nextTick(() => {
        const otpComponent = this.$children.find(child => child.$options.name === 'OtpInput');
        if (otpComponent) {
          // Try multiple methods to clear the OTP input
          if (otpComponent.clearInput) {
            otpComponent.clearInput();
          }
          if (otpComponent.handleClear) {
            otpComponent.handleClear();
          }
          // Force clear by setting otp value directly
          if (otpComponent.otp) {
            otpComponent.otp = [];
          }
          // Trigger component update
          otpComponent.$forceUpdate();
        }
      });
    },

    startInactivityTimer() {
      this.clearInactivityTimer();
      // Clear MFA after 2 minutes of inactivity
      this.inactivityTimer = setTimeout(() => {
        if (this.visible) {
          this.clearMfaCode();
          this.$emit('clear-error');
        }
      }, 120000); // 2 minutes
    },

    clearInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }
    },

    clearTimers() {
      this.clearInactivityTimer();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.mfa-verification-modal {
  .popup-wrapper {
    max-width: rem(450);
    padding: $space-8 $space-10;

    .close-action {
      float: right;

      .close_btn {
        float: right;
        margin-top: -$space-4;
        margin-right: -$space-4;

        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }

    .modal-content {
      .title {
        color: var(--text-primary);
        font-size: $font-size-base;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $font-size-md;
        text-transform: uppercase;
        padding-bottom: $space-4;
        text-align: center;
      }

      .description {
        font-weight: normal;
        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: $font-size-lg;
        text-align: center;
        margin-bottom: $space-6;
      }

      .mfa-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: $space-5;

        .otp-code {
          justify-content: center;
          margin-bottom: $space-4;
        }

        .error-message {
          color: var(--severity-critical);
          font-size: $font-size-sm;
          line-height: $font-size-base;
          text-align: center;
        }
      }

      .buttons {
        text-align: right;
        display: flex;
        gap: $space-2;
        justify-content: flex-end;

        // Override Vuetify button styles
        :deep(.v-btn) {
          border-radius: $radius-full;
        }

        .back-button.back-button {
          box-sizing: border-box;
          height: rem(36);
          width: rem(86);
          border: $border-width-medium solid var(--border-secondary);
          color: var(--text-secondary);
          border-radius: $radius-full;
          background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
          box-shadow: $shadow-sm;
          font-size: $font-size-sm;
          font-weight: 600;
          transition: all 0.3s ease;
          text-transform: none;
          letter-spacing: normal;

          :deep(.v-btn__content) {
            color: var(--text-secondary);
          }

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
            box-shadow: $shadow-md;
          }
        }

        .verify-button.verify-button {
          height: rem(36);
          width: fit-content;
          min-width: rem(140);
          border-radius: $radius-full;
          background: linear-gradient(135deg, var(--brand-primary) 0%, #a855f7 100%);
          box-shadow: 0 3px 8px rgba(143, 36, 208, 0.3);
          border: none;
          color: var(--text-inverse);
          font-size: $font-size-sm;
          font-weight: 600;
          letter-spacing: 0.5px;
          line-height: $font-size-md;
          text-align: center;
          text-transform: none;
          transition: all 0.3s ease;

          :deep(.v-btn__content) {
            color: var(--text-inverse);
          }

          &:hover:not(:disabled) {
            box-shadow: 0 4px 12px rgba(143, 36, 208, 0.4);
          }

          &:disabled {
            background: var(--bg-tertiary);
            color: var(--text-muted);
            box-shadow: none;

            :deep(.v-btn__content) {
              color: var(--text-muted);
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.mfa-verification-modal .otp-input {
  width: rem(40);
  height: rem(40);
  padding: $space-1;
  margin: 0 $space-2;
  font-size: $font-size-xl;
  border-radius: $radius-xs;
  border: $border-width-thin solid var(--border-primary);
  text-align: center;
  outline: none;

  &.error {
    border: $border-width-thin solid var(--severity-critical);
  }

  &:focus {
    border-color: var(--brand-primary);
  }
}

.mfa-verification-modal .otp-input::-webkit-inner-spin-button,
.mfa-verification-modal .otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.mfa-verification-modal .otp-code div:nth-child(4) .otp-input {
  margin-left: $space-8;
}

.mfa-verification-modal .incorrect-code .otp-input {
  border-color: var(--severity-critical);
}
</style>
